'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { BiArrowToLeft, BiSend, BiSolidBadgeDollar, BiX } from 'react-icons/bi';
import QwikFixCard from './QwikFixCard';
import SpecialtyContractorCard from './SpecialtyContractorCard';
import {
  buildSpecialtyCompanyAssignments,
  getQwikFixScopedItems,
  getQuoteRecipientCompanyCount,
  getSpecialtyContractorGroups,
  hasPendingQuoteRequests,
} from './mapRepairDataForQuickFixModal';
import {
  buildUnifiedQuoteEntries,
  getQuoteEntryItemCount,
} from '@/app/client/components/repair-list-quote-entries/quoteEntryUtils';
import RepairListQuoteEntryList from '@/app/client/components/repair-list-quote-entries/RepairListQuoteEntryList';
import { isQwikFixQuoteSubmission } from '@/util/functions/repairList/isQwikFixQuoteSubmission';
import { formatPhoneToE164US } from '@/util/functions/formatPhoneToE164US';
import type { QuickFixRepairQuoteModalProps, QwikFixPhoneContact } from './types';
import type { QuoteRequest, QuoteSubmission } from '@/util/types/serverTypeCollection/repairList';
import type { RepairCompanyRaw } from '@/util/types/serverTypeCollection/repairCompanies';

/** Stable defaults — inline `= []` creates a new reference each render when props are undefined. */
const EMPTY_QUOTE_SUBMISSIONS: QuoteSubmission[] = [];
const EMPTY_QUOTE_REQUESTS: QuoteRequest[] = [];
const EMPTY_ITEMS: NonNullable<QuickFixRepairQuoteModalProps['items']> = [];
const EMPTY_SPECIALTY_CONTRACTORS: NonNullable<
  QuickFixRepairQuoteModalProps['specialtyContractors']
> = [];
const EMPTY_REPAIR_COMPANIES: RepairCompanyRaw[] = [];

/** Flip to true to preview the success screen locally. */
const DEBUG_SHOW_SUCCESS = false;

function contractorsSentFromQuoteRequests(quoteRequests: QuoteRequest[]) {
  return Object.fromEntries(
    quoteRequests
      .map((quote) => quote._repairCompanyId)
      .filter(Boolean)
      .map((companyId) => [companyId, true]),
  );
}

export default function QuickFixRepairQuoteModal({
  isOpen,
  onClose,
  quoteSubmissions = EMPTY_QUOTE_SUBMISSIONS,
  quoteRequests = EMPTY_QUOTE_REQUESTS,
  repairListId,
  reportId,
  buildRepairListViewUrl,
  buildReportUrl,
  canViewQuoteDetails = true,
  benefitsLayout = 'grid',
  items = EMPTY_ITEMS,
  specialtyContractors = EMPTY_SPECIALTY_CONTRACTORS,
  repairCompanies = EMPTY_REPAIR_COMPANIES,
  onSendQwikFix,
  onCheckQwikFixPhone,
  onSaveQwikFixPhone,
  onSendContractor,
  onSendSpecialtyQuotes,
  companyName,
  reportDeficiencyCount = 0,
  learnMoreHref = '#',
  isSampleReport = false,
}: QuickFixRepairQuoteModalProps) {
  const [qwikSent, setQwikSent] = useState(false);
  const [contractorsSent, setContractorsSent] = useState<Record<string, boolean>>({});
  const [shareWithClient, setShareWithClient] = useState(true);
  const [quoteWholeReport, setQuoteWholeReport] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quotesRequestComplete, setQuotesRequestComplete] = useState(false);
  const [qwikError, setQwikError] = useState<string | null>(null);
  const [phonePromptContact, setPhonePromptContact] = useState<QwikFixPhoneContact | null>(null);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [pendingQwikRequest, setPendingQwikRequest] = useState<{
    itemIds: string[];
    options: { shareWithClient: boolean };
    sendSpecialtyToo: boolean;
  } | null>(null);
  const [menuPortalContainer, setMenuPortalContainer] = useState<Element>();

  const setMenuPortalRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const container =
      node.closest('[role="dialog"]') ??
      node.closest('section') ??
      node.closest('[data-slot="wrapper"]');
    if (container) setMenuPortalContainer(container);
  }, []);

  const hasQwikFixSubmission = quoteSubmissions.some(isQwikFixQuoteSubmission);
  const quoteEntries = useMemo(
    () => buildUnifiedQuoteEntries({ quoteSubmissions, quoteRequests }),
    [quoteSubmissions, quoteRequests],
  );
  const hasPersistedQuotes = quoteEntries.length > 0;
  const modalSessionActiveRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      modalSessionActiveRef.current = false;
      return;
    }

    const justOpened = !modalSessionActiveRef.current;
    modalSessionActiveRef.current = true;

    if (justOpened) {
      setShareWithClient(true);
      setQuoteWholeReport(false);
      setIsSubmitting(false);
      setQuotesRequestComplete(false);
      setQwikError(null);
      setPhonePromptContact(null);
      setPhoneInput('');
      setPhoneError(null);
      setPhoneSaving(false);
      setPendingQwikRequest(null);
    }

    setQwikSent(hasQwikFixSubmission);
    setContractorsSent(contractorsSentFromQuoteRequests(quoteRequests));
  }, [isOpen, hasQwikFixSubmission, quoteRequests]);

  const qwikItems = useMemo(
    () => getQwikFixScopedItems(items, quoteWholeReport),
    [items, quoteWholeReport],
  );

  const specialtyContractorGroups = useMemo(
    () => getSpecialtyContractorGroups(items, repairCompanies, specialtyContractors),
    [items, repairCompanies, specialtyContractors],
  );

  const hasPendingQuotes = hasPendingQuoteRequests({
    qwikItems,
    qwikSent,
    specialtyContractorGroups,
    contractorsSent,
  });
  const showSuccessState =
    DEBUG_SHOW_SUCCESS ||
    quotesRequestComplete ||
    (hasPersistedQuotes && !hasPendingQuotes);

  const pendingSpecialtyGroupCount = useMemo(
    () =>
      specialtyContractorGroups.filter(
        ({ contractor }) => !contractorsSent[contractor.id],
      ).length,
    [contractorsSent, specialtyContractorGroups],
  );

  const quoteCompanyCount = useMemo(
    () =>
      getQuoteRecipientCompanyCount(qwikItems.length, pendingSpecialtyGroupCount, {
        includeQwikFix: !qwikSent,
      }),
    [pendingSpecialtyGroupCount, qwikItems.length, qwikSent],
  );

  const submitQwikRequest = async (itemIds: string[], options: { shareWithClient: boolean }) => {
    await onSendQwikFix?.(itemIds, options);
    setQwikSent(true);
  };

  const submitSpecialtyRequests = async (
    assignments: Record<string, string[]>,
  ) => {
    if (Object.keys(assignments).length === 0) return;
    await onSendSpecialtyQuotes?.(assignments);
    setContractorsSent((prev) => ({
      ...prev,
      ...Object.fromEntries(Object.keys(assignments).map((id) => [id, true])),
    }));
  };

  const handleRequestRepairQuotes = async () => {
    if (isSampleReport || isSubmitting || phoneSaving || quoteCompanyCount === 0) return;

    // Freeze preference before any async work or parent re-renders from prop updates.
    const qwikFixOptions = { shareWithClient };

    const itemIds = qwikItems.map((item) => item.id);
    const shouldSendQwik = itemIds.length > 0 && !qwikSent;
    const specialtyAssignments = buildSpecialtyCompanyAssignments(
      specialtyContractorGroups,
      contractorsSent,
    );
    const shouldSendSpecialty = Object.keys(specialtyAssignments).length > 0;

    if (!shouldSendQwik && !shouldSendSpecialty) return;

    setIsSubmitting(true);
    setQwikError(null);

    try {
      if (shouldSendQwik) {
        const options = qwikFixOptions;
        const requirement = await onCheckQwikFixPhone?.(options);
        if (requirement?.needsPhone) {
          if (!requirement.contact) {
            throw new Error('QwikFix needs a phone number, but no contact was returned.');
          }
          setPendingQwikRequest({
            itemIds,
            options,
            sendSpecialtyToo: shouldSendSpecialty,
          });
          setPhonePromptContact(requirement.contact);
          setPhoneInput(requirement.contact.phone || '');
          setPhoneError(null);
          return;
        }
        await submitQwikRequest(itemIds, options);
      }

      if (shouldSendSpecialty) {
        await submitSpecialtyRequests(specialtyAssignments);
      }

      if (
        !hasPendingQuoteRequests({
          qwikItems,
          qwikSent: qwikSent || shouldSendQwik,
          specialtyContractorGroups,
          contractorsSent: shouldSendSpecialty
            ? {
                ...contractorsSent,
                ...Object.fromEntries(Object.keys(specialtyAssignments).map((id) => [id, true])),
              }
            : contractorsSent,
        })
      ) {
        setQuotesRequestComplete(true);
      }
    } catch (err: unknown) {
      setQwikError(
        err instanceof Error ? err.message : 'Failed to send repair quote requests',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelPhonePrompt = () => {
    setPhonePromptContact(null);
    setPhoneInput('');
    setPhoneError(null);
    setPendingQwikRequest(null);
  };

  const handleSavePhoneAndSend = async () => {
    if (!phonePromptContact || !pendingQwikRequest || phoneSaving || isSubmitting) return;
    const normalizedPhone = formatPhoneToE164US(phoneInput);
    if (!normalizedPhone) {
      setPhoneError('Enter a valid US phone number.');
      return;
    }

    setPhoneSaving(true);
    setPhoneError(null);
    try {
      await onSaveQwikFixPhone?.(phonePromptContact.id, normalizedPhone);
      const pending = pendingQwikRequest;
      setPhonePromptContact(null);
      setPhoneInput('');
      setPendingQwikRequest(null);
      setIsSubmitting(true);
      await submitQwikRequest(pending.itemIds, pending.options);
      const specialtyAssignments = pending.sendSpecialtyToo
        ? buildSpecialtyCompanyAssignments(specialtyContractorGroups, contractorsSent)
        : {};
      if (pending.sendSpecialtyToo) {
        await submitSpecialtyRequests(specialtyAssignments);
      }
      if (
        !hasPendingQuoteRequests({
          qwikItems,
          qwikSent: true,
          specialtyContractorGroups,
          contractorsSent: {
            ...contractorsSent,
            ...Object.fromEntries(Object.keys(specialtyAssignments).map((id) => [id, true])),
          },
        })
      ) {
        setQuotesRequestComplete(true);
      }
    } catch (err: unknown) {
      setPhoneError(err instanceof Error ? err.message : 'Failed to save phone number');
    } finally {
      setPhoneSaving(false);
      setIsSubmitting(false);
    }
  };

  const handleSendContractor = async (id: string) => {
    if (isSampleReport) return;
    if (contractorsSent[id] || isSubmitting) return;
    const group = specialtyContractorGroups.find(({ contractor }) => contractor.id === id);
    if (!group) return;

    setIsSubmitting(true);
    setQwikError(null);
    try {
      const assignments = { [id]: group.items.map((item) => item.id) };
      if (onSendSpecialtyQuotes) {
        await submitSpecialtyRequests(assignments);
      } else {
        await onSendContractor?.(id);
        setContractorsSent((prev) => ({ ...prev, [id]: true }));
      }
    } catch (err: unknown) {
      setQwikError(
        err instanceof Error ? err.message : 'Failed to send repair quote request',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const quoteOptionCount = specialtyContractorGroups.length + 1;
  const quoteReadySubtext = `${items.length} new ${items.length === 1 ? 'item' : 'items'} ready to quote · ${quoteOptionCount} ${quoteOptionCount === 1 ? 'solution' : 'solutions'}`;

  const sentContractorCount = quoteEntries.length;
  const sentItemCount = quoteEntries.reduce(
    (sum, entry) => sum + getQuoteEntryItemCount(entry),
    0,
  );
  const successHeaderSubtext =
    sentContractorCount > 0
      ? `Sent to ${sentContractorCount} ${sentContractorCount === 1 ? 'contractor' : 'contractors'} · ${sentItemCount} ${sentItemCount === 1 ? 'item' : 'items'}`
      : 'Your repair quote requests are on the way';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='2xl'
      scrollBehavior='inside'
      classNames={{
        base: 'max-h-[min(90vh,calc(100vh-48px))] max-w-[720px] !z-[999999] overflow-hidden rounded-[14px] border border-default-200 bg-white shadow-2xl',
        wrapper: 'z-[999999]',
        header: 'shrink-0 border-b border-default-200',
        body: 'min-h-0 flex-1 gap-0 overflow-x-hidden overflow-y-auto',
        footer: 'shrink-0 border-t border-default-200',
        closeButton: 'hidden',
      }}
      backdrop='blur'
    >
      <ModalContent>
        <ModalHeader className='flex items-start gap-3 px-[22px] py-3'>
          <div className='grid h-9 w-9 shrink-0 place-items-center self-center rounded-[10px] bg-warning/10 text-warning'>
            <BiSolidBadgeDollar size={20} />
          </div>
          <div className='min-w-0 flex-1'>
            <h2
              id='quickfix-repair-quote-title'
              className='font-mono text-lg font-bold leading-tight tracking-tight text-foreground'
            >
              {showSuccessState ? 'Repair Quotes Sent' : 'Get Repair Quotes'}
            </h2>
            <p className='text-xs leading-snug text-default-500'>
              {showSuccessState ? successHeaderSubtext : quoteReadySubtext}
            </p>
          </div>
          <button
            type='button'
            onClick={onClose}
            aria-label='Close'
            className='grid h-7 w-7 shrink-0 cursor-pointer place-items-center self-center rounded-md text-default-500 transition hover:bg-default-100 hover:text-foreground'
          >
            <BiX size={18} />
          </button>
        </ModalHeader>

        <ModalBody className='px-[22px] py-[18px]'>
          {isSampleReport && !showSuccessState && (
            <Alert color='warning' variant='flat' className='mb-4'>
              Sample report — QwikFix and specialty contractor sends are disabled. Use Thumbtack on
              the overview page to explore the quote flow with test credentials.
            </Alert>
          )}
          {showSuccessState ? (
            <div className='py-6'>
              <div className='mb-6 flex flex-col items-center text-center'>
                <div className='mb-3 grid h-12 w-12 place-items-center rounded-full bg-success/10 text-2xl text-success'>
                  ✓
                </div>
                <h2 className='text-xl font-bold text-foreground'>Repair quote requests sent</h2>
                <p className='mt-1 text-sm text-default-500'>
                  Contractors will follow up with quotes.
                </p>
              </div>

              {repairListId && buildRepairListViewUrl && quoteEntries.length > 0 && (
                <div ref={setMenuPortalRef}>
                  <p className='mb-2 text-[11px] font-semibold uppercase tracking-wider text-default-500'>
                    Sent to
                  </p>
                  <RepairListQuoteEntryList
                    quoteSubmissions={quoteSubmissions}
                    quoteRequests={quoteRequests}
                    repairListId={repairListId}
                    reportId={reportId}
                    repairCompanies={repairCompanies}
                    canViewQuoteDetails={canViewQuoteDetails}
                    buildReportUrl={buildReportUrl}
                    buildRepairListViewUrl={buildRepairListViewUrl}
                    menuPortalContainer={menuPortalContainer}
                  />
                </div>
              )}
            </div>
          ) : phonePromptContact ? (
            <div className='py-4'>
              <div className='rounded-[14px] border border-warning-200 bg-warning-50 p-5'>
                <p className='text-sm font-bold text-warning-900'>
                  QwikFix needs a client phone number
                </p>
                <p className='mt-1 text-sm text-warning-800'>
                  Add a valid phone number for {phonePromptContact.name} before sending this
                  request. We will save it to the contact for future QwikFix requests.
                </p>
              </div>

              <div className='mt-5 rounded-[12px] border border-default-200 bg-white p-4'>
                <p className='text-xs font-semibold uppercase tracking-wider text-default-500'>
                  Contact
                </p>
                <p className='mt-1 text-sm font-semibold text-foreground'>
                  {phonePromptContact.name}
                </p>
                <p className='text-xs text-default-500'>{phonePromptContact.email}</p>

                <Input
                  className='mt-4'
                  label='Phone number'
                  placeholder='(555) 555-5555'
                  type='tel'
                  value={phoneInput}
                  onValueChange={(value) => {
                    setPhoneInput(value);
                    setPhoneError(null);
                  }}
                  isInvalid={Boolean(phoneError)}
                  errorMessage={phoneError || undefined}
                  isDisabled={phoneSaving || isSubmitting}
                />
              </div>
            </div>
          ) : (
            <>
              <QwikFixCard
                allItems={items}
                benefitsLayout={benefitsLayout}
                sent={qwikSent}
                isSending={isSubmitting}
                onSend={handleRequestRepairQuotes}
                shareWithClient={shareWithClient}
                onShareWithClientChange={setShareWithClient}
                quoteWholeReport={quoteWholeReport}
                onQuoteWholeReportChange={setQuoteWholeReport}
                companyName={companyName}
                reportDeficiencyCount={reportDeficiencyCount}
                sendDisabled={isSampleReport}
              />

              {qwikError && (
                <Alert color='danger' variant='flat' className='mb-3'>
                  {qwikError}
                </Alert>
              )}

              {specialtyContractorGroups.length > 0 && (
                <section className='mt-4'>
                  <div className='mb-3 flex items-center gap-2.5'>
                    <span className='h-px flex-1 bg-default-200' />
                    <span className='text-[11px] font-semibold uppercase tracking-wider text-default-500'>
                      Use our specialty contractors for items QwikFix doesn&apos;t cover
                    </span>
                    <span className='h-px flex-1 bg-default-200' />
                  </div>
                  {specialtyContractorGroups.map(({ contractor, items: contractorItems }) => (
                    <SpecialtyContractorCard
                      key={contractor.id}
                      contractor={contractor}
                      items={contractorItems}
                      sent={!!contractorsSent[contractor.id]}
                      onSend={() => handleSendContractor(contractor.id)}
                      sendDisabled={isSampleReport}
                    />
                  ))}
                </section>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter className='flex flex-col gap-3 px-[22px] py-3.5 sm:flex-row sm:items-center sm:justify-between'>
          {showSuccessState ? (
            <button
              type='button'
              onClick={onClose}
              className='mx-auto cursor-pointer rounded-lg bg-main px-8 py-2.5 text-[13px] font-bold uppercase text-white shadow-[0_1px_2px_rgba(215,84,0,0.3),0_4px_12px_-2px_rgba(215,84,0,0.3)] transition hover:bg-[#b94800]'
            >
              Done
            </button>
          ) : phonePromptContact ? (
            <div className='flex w-full items-center justify-end gap-2.5'>
              <button
                type='button'
                onClick={handleCancelPhonePrompt}
                disabled={phoneSaving || isSubmitting}
                className='inline-flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2.5 text-[13px] font-semibold text-default-500 transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60'
              >
                <BiArrowToLeft size={16} />
                Back
              </button>
              <Button
                color='warning'
                variant='solid'
                onPress={handleSavePhoneAndSend}
                isLoading={phoneSaving || isSubmitting}
                isDisabled={phoneSaving || isSubmitting}
                className='min-w-0 cursor-pointer px-4 text-[13px] font-semibold'
                startContent={!phoneSaving && !isSubmitting ? <BiSend size={16} /> : undefined}
              >
                {phoneSaving || isSubmitting ? 'Saving phone...' : 'Save Phone & Send'}
              </Button>
            </div>
          ) : (
            <div className='flex w-full shrink-0 items-center justify-end gap-2.5'>
                <button
                  type='button'
                  onClick={onClose}
                  className='cursor-pointer rounded-lg px-4 py-2.5 text-[13px] font-semibold text-default-500 transition hover:text-foreground'
                >
                  Cancel
                </button>
                <Button
                  color='warning'
                  variant='solid'
                  onPress={handleRequestRepairQuotes}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting || quoteCompanyCount === 0 || isSampleReport}
                  aria-label={
                    isSubmitting
                      ? 'Sending to all'
                      : quoteCompanyCount > 0
                        ? `Send to all (${quoteCompanyCount})`
                        : 'Send to all'
                  }
                  className='min-w-0 cursor-pointer px-4 text-[13px] font-bold uppercase'
                  startContent={!isSubmitting ? <BiSend size={16} /> : undefined}
                >
                  {isSubmitting
                    ? 'Sending...'
                    : quoteCompanyCount > 0
                      ? `Send to All (${quoteCompanyCount})`
                      : 'Send to All'}
                </Button>
            </div>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
